import Layout from "../../components/blog/Layout";
import { getPostData, getSortedPostsData } from "../../lib/posts/aktualnosci";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/pl";
import { serialize } from "next-mdx-remote/serialize";
import { MDXRemote } from "next-mdx-remote";
import Section from "../../components/Section";
import DeclarationBox from "../../components/DeclarationBox";
import DeclarationBoxItem from "../../components/DeclarationBoxItem";
import BackgroundSection from "../../components/BackgroundSection";
import SectionBox from "../../components/SectionBox";
import Author from "../../components/Author";
import Link from "next/link";
import BackButton from "../../components/BackButton";
import FAQ from "../../components/FAQ";
import { ArticleMetaProvider } from "../../components/ArticleMetaContext";
import {
	FloatVideo,
	ArticleVideo,
	DeclarationBoxIndividual,
	DeclarationModal,
} from "../../components/DynamicComponents";
import MDXComponents from "../../components/MDXComponents";
import ArticleStructuredData from "../../components/ArticleStructuredData";
import { resolveArticleAuthor } from "../../lib/authors";

dayjs.extend(localizedFormat);
dayjs.locale("pl");

const components = {
	...MDXComponents,
	FloatVideo,
	ArticleVideo,
	DeclarationBoxIndividual,
	BackgroundSection,
	SectionBox,
	Section,
	FAQ,
};

export async function getServerSideProps({ params, res }) {
	res.setHeader("Cache-Control", "no-store, max-age=0");

	const postData = await getPostData(params.id);

	if (!postData) {
		return {
			notFound: true,
		};
	}

	const allPostsData = getSortedPostsData();

	const mdxSource = await serialize(postData.contentHtml, {
		mdxOptions: {
			remarkPlugins: [],
			rehypePlugins: [],
		},
	});

	const relatedPosts = allPostsData.filter((item) => item.id !== params.id).slice(0, 3);

	return {
		props: {
			postData,
			relatedPosts,
			source: mdxSource,
		},
	};
}

export default function Post({ postData, relatedPosts, source }) {
	const blogMiasto = "PRZYJAZNEDEKLARACJE";
	const blogPodatek = postData.blogPodatek;
	const resolvedAuthor = resolveArticleAuthor(postData);

	const generatePathWithQuery = (id) => {
		if (id === "konkurs-forum-skarbnikow-2024-o-konkursie") {
			return `/aktualnosci/${id}/?utm_source=konkurs24&utm_medium=link&utm_campaign=zainteresowane`;
		} else if (id === "konkurs-forum-skarbnikow-2024-ogloszenie-wynikow") {
			return `/aktualnosci/${id}/?utm_source=konkurs24&utm_medium=link&utm_campaign=wyniki`;
		} else {
			return `/aktualnosci/${id}/`;
		}
	};

	return (
		<Layout
			article
			title={postData.title}
			description={postData.description}
			seoTitle={postData.seoTitle}
			seoDescription={postData.seoDescription}
			seoImage={postData.seoImage}
			imageDesktop={postData.imageDesktop}
			imageTablet={postData.imageTablet}
			imageMobile={postData.imageMobile}
			datePublication={postData.datePublication}
			dateUpdate={postData.dateUpdate}
			hours={postData.hours}
			author={resolvedAuthor.name}
			authorId={resolvedAuthor.id}
			authorVariable={resolvedAuthor.legacyVariable}
			blogMiasto={postData.blogMiasto}
			blogPodatek={postData.blogPodatek}
			blogWithAction={postData.blogWithAction}
			blogActionText={postData.blogActionText}
			blogActionUrl={postData.blogActionUrl}
		>
			<ArticleStructuredData postData={postData} section="aktualnosci" />

			<section className="pd-article__container">
				<article className="pd-article">
					<ArticleMetaProvider value={postData}>
						<MDXRemote {...source} components={components} />
					</ArticleMetaProvider>

					<aside className="pd-entries__popular absolute">
						<div className="pd-entries__max-width">
							{resolvedAuthor.id !== "redakcja" && (
								<>
									<h2 className="pd-entries__title">Autor artykułu:</h2>
									<Author author={resolvedAuthor.id} />
								</>
							)}
							<h2 className="pd-entries__title">Podobne wpisy:</h2>
							{relatedPosts.map(({ id, datePublication, title, author }, index) => (
								<article key={index} className="pd-entry">
									<header className="pd-entry__header">
										<h3 className="pd-entry__title">
											<Link
												href={generatePathWithQuery(id)}
												aria-label={`Czytaj więcej o ${title.toLowerCase()}`}
											>
												{title}
											</Link>
										</h3>
									</header>
									<p className="pd-entry__meta">
										<span className="pd-entry__meta__date">
											<time dateTime={dayjs(datePublication).format("YYYY-MM-DD")}>
												{dayjs(datePublication).format("D MMMM YYYY")}
											</time>
										</span>
										<span className="pd-entry__meta__author">{author}</span>
									</p>
									<footer className="pd-entry__footer">
										<Link
											href={generatePathWithQuery(id)}
											aria-label={`Czytaj więcej o ${title.toLowerCase()}`}
											className="pd-entry__readmore"
										>
											Czytaj więcej
										</Link>
									</footer>
								</article>
							))}
						</div>
					</aside>
				</article>
			</section>

			<Section>
				<BackButton />
			</Section>

			{postData.dialog === "only" && (
				<DeclarationModal miasto={postData.blogMiasto} podatek={postData.blogPodatek} />
			)}

			{postData.dialog === "full" && (
				<Section id="wypelnij-deklaracje" title="sections.wypelnijdeklaracje.title">
					<DeclarationBox center>
						<DeclarationBoxItem
							miasto={postData.blogMiasto}
							podatek={postData.blogPodatek}
							noReadMore
						/>
					</DeclarationBox>
					<DeclarationModal miasto={postData.blogMiasto} />
				</Section>
			)}
		</Layout>
	);
}
